/**
 * Signed create / cancel / list against the off-chain TP/SL trigger executor.
 *
 * Signing follows the house EIP-712 pattern (ethers v5 `_signTypedData` on a
 * `Web3Provider` built from the active EIP-1193 provider — see
 * `usePerpOrder.gasless.ts`). The executor recovers the signer and compares it
 * to `trader`, so the CONNECTED wallet must sign; the one-click agent key is
 * deliberately NOT used here (it would recover to the agent, not the trader).
 *
 * Support is gated twice: statically by `ChainConfig.supportsTpsl` and at
 * runtime by a 503 `tpsl unavailable` from any `/tpsl` route.
 */
import { useCallback, useContext, useMemo, useState } from "react";
import { ethers } from "ethers";
import useSWR from "swr";
import { getCurrentChainConfig } from "../../config/chains";
import { AuthContext } from "../../contexts/AuthContext";
import { APIEndpointEnum } from "../../enums/endpoint";
import { getWeb3ProviderForChain } from "../../utils/wallet/activeSigner";
import {
  cancelTpslOrder,
  createTpslOrders,
  getTpslNonce,
  getTpslOrders,
  isTpslUnavailable,
} from "./tpsl.client";
import {
  CANCEL_TRIGGER_TYPES,
  CREATE_TRIGGERS_TYPES,
  computeOrdersHash,
  marginModeToUint8,
  tpslDeadline,
  tpslDomain,
  triggerIdToBytes32,
} from "./tpsl.signing";
import { TpslLeg, TpslMarginMode, TpslOrder } from "./tpsl.types";

export interface CreateTriggersArgs {
  market: string;
  isLong: boolean;
  marginMode: TpslMarginMode;
  ocoLinked: boolean;
  legs: TpslLeg[];
}

/**
 * The executor's verifying contract is the chain's perp proxy router
 * (`PERP_PROXY_ROUTER` in the deployment manifest, surfaced as `perpRouter`).
 */
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

/** Placeholder addresses are as good as absent — see getTpslVerifyingContract. */
const isUsableAddress = (value?: string): boolean =>
  Boolean(value) && value!.toLowerCase() !== ZERO_ADDRESS;

export const getTpslVerifyingContract = (): string | undefined => {
  const dex = getCurrentChainConfig().contracts.perpetualDex;
  // Reject the zero address explicitly rather than relying on truthiness: these are
  // STRINGS, so "0x0000…0000" is truthy. A chain that is scaffolded but not yet
  // deployed carries exactly that placeholder for both fields — without this,
  // enabling supportsTpsl there would pass the support check and sign EIP-712
  // payloads against the zero address.
  if (isUsableAddress(dex?.perpRouter)) return dex?.perpRouter;
  if (isUsableAddress(dex?.orderHandler)) return dex?.orderHandler;
  return undefined;
};

export function useTpslApi() {
  const { account } = useContext(AuthContext);
  const config = getCurrentChainConfig();
  const verifyingContract = getTpslVerifyingContract();
  const [runtimeUnavailable, setRuntimeUnavailable] = useState(false);

  const chainSupported = Boolean(config.supportsTpsl && verifyingContract);
  const trader = account || undefined;

  // Doubles as the availability probe: a 503 here flips the feature off.
  const swr = useSWR<TpslOrder[], Error>(
    chainSupported && trader
      ? [APIEndpointEnum.TPSL_ORDERS, trader.toLowerCase()]
      : null,
    () => getTpslOrders(trader as string),
    {
      refreshInterval: 30_000,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      keepPreviousData: false,
    }
  );

  const supported =
    chainSupported && !runtimeUnavailable && !isTpslUnavailable(swr.error);

  const domain = useMemo(
    () =>
      verifyingContract
        ? tpslDomain(config.chainId, verifyingContract)
        : undefined,
    [config.chainId, verifyingContract]
  );

  const requireReady = useCallback(() => {
    if (!supported || !domain) {
      throw new Error("TP/SL is not available on this network.");
    }
    if (!trader) {
      throw new Error("Connect a wallet to manage TP/SL.");
    }
    return { domain, trader };
  }, [domain, supported, trader]);

  /** Latch a 503 so the UI hides instead of retrying forever. */
  const rethrow = useCallback((error: unknown): never => {
    if (isTpslUnavailable(error)) {
      setRuntimeUnavailable(true);
    }
    throw error;
  }, []);

  const signTypedData = useCallback(
    async (types: Record<string, unknown>, message: Record<string, unknown>) => {
      const provider = await getWeb3ProviderForChain(config);
      // Pass the trader explicitly. A bare `getSigner()` leaves ethers' internal
      // `_address` empty and `_index = 0`, so `getAddress()` resolves the signer by
      // querying `eth_accounts` — and any provider that does not enumerate accounts
      // (locked wallet, embedded/smart wallet, an RPC-backed transport) returns [],
      // which fails `0 <= 0` and throws the opaque
      //   unknown account #0 (operation="getAddress", UNSUPPORTED_OPERATION)
      // before the signature request ever reaches the wallet. Supplying the address
      // short-circuits that lookup entirely. Callers reach here only via
      // requireReady(), which guarantees `trader` is set.
      // Matches utils/wallet/ethersAdapter.ts, which already does this.
      return provider
        .getSigner(trader)
        ._signTypedData(domain as any, types as any, message);
    },
    [domain, trader]
  );

  /** One POST carrying 1-2 legs. `ocoLinked` should be true when both are set. */
  const createTriggers = useCallback(
    async ({
      market,
      isLong,
      marginMode,
      ocoLinked,
      legs,
    }: CreateTriggersArgs): Promise<TpslOrder[]> => {
      const ready = requireReady();
      if (!legs.length || legs.length > 2) {
        throw new Error("TP/SL accepts one or two triggers at a time.");
      }

      const marketAddress = market.toLowerCase();
      try {
        const nonce = await getTpslNonce(ready.trader);
        const deadline = tpslDeadline();

        const signature = await signTypedData(CREATE_TRIGGERS_TYPES, {
          trader: ready.trader,
          market: marketAddress,
          isLong,
          marginMode: marginModeToUint8(marginMode),
          ordersHash: computeOrdersHash(legs),
          expiresAt: 0,
          nonce,
          deadline,
        });

        return await createTpslOrders({
          trader: ready.trader,
          market: marketAddress,
          isLong,
          marginMode,
          ocoLinked,
          orders: legs,
          auth: { kind: "eip712", nonce, deadline, signature },
        });
      } catch (error) {
        return rethrow(error);
      }
    },
    [requireReady, rethrow, signTypedData]
  );

  const cancelTrigger = useCallback(
    async (triggerId: string): Promise<void> => {
      const ready = requireReady();
      try {
        const nonce = await getTpslNonce(ready.trader);
        const deadline = tpslDeadline();

        const signature = await signTypedData(CANCEL_TRIGGER_TYPES, {
          trader: ready.trader,
          triggerId: triggerIdToBytes32(triggerId),
          nonce,
          deadline,
        });

        await cancelTpslOrder(triggerId, {
          trader: ready.trader,
          auth: { kind: "eip712", nonce, deadline, signature },
        });
      } catch (error) {
        rethrow(error);
      }
    },
    [requireReady, rethrow, signTypedData]
  );

  const { mutate: refreshOrders } = swr;
  const openOrders = useMemo(() => swr.data || [], [swr.data]);

  return useMemo(
    () => ({
      supported,
      trader,
      openOrders,
      refreshOrders,
      createTriggers,
      cancelTrigger,
    }),
    [
      supported,
      trader,
      openOrders,
      refreshOrders,
      createTriggers,
      cancelTrigger,
    ]
  );
}
